import { useState, useCallback } from 'react'
import { useWorkspaces } from '../hooks/useWorkspaces.js'
import WorkspaceCard from '../components/WorkspaceCard.jsx'
import CreateWorkspaceModal from '../components/CreateWorkspaceModal.jsx'

function EmptyState({ onOpen }) {
  return (
    <div className="col-span-full flex flex-col items-center justify-center py-24 border border-dashed border-border rounded-xl gap-4">
      <div className="w-12 h-12 rounded-xl bg-surface2 border border-border flex items-center justify-center">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <rect x="1" y="1" width="8" height="8" rx="1" stroke="#e8a020" strokeWidth="1.5" opacity="0.7" />
          <rect x="11" y="1" width="8" height="8" rx="1" stroke="#e8a020" strokeWidth="1.5" opacity="0.35" />
          <rect x="1" y="11" width="8" height="8" rx="1" stroke="#e8a020" strokeWidth="1.5" opacity="0.35" />
          <rect x="11" y="11" width="8" height="8" rx="1" stroke="#e8a020" strokeWidth="1.5" opacity="0.15" />
        </svg>
      </div>
      <div className="text-center">
        <p className="text-sm text-text mb-1">No workspaces yet</p>
        <p className="text-xs text-text-dim max-w-xs leading-relaxed">
          A workspace pairs an agent with a directory. Create one to start recording state snapshots.
        </p>
      </div>
      <button
        onClick={onOpen}
        className="px-4 py-2 text-xs font-medium bg-accent text-ground rounded hover:bg-accent-dim transition-colors"
      >
        Create first workspace
      </button>
    </div>
  )
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="col-span-full flex flex-col items-center justify-center py-16 gap-3">
      <p className="text-sm text-red">Could not load workspaces</p>
      <p className="text-xs text-text-dim font-mono">{message}</p>
      <button
        onClick={onRetry}
        className="text-xs text-accent hover:underline"
      >
        Try again
      </button>
    </div>
  )
}

export default function Dashboard() {
  const { workspaces, loading, error, refresh } = useWorkspaces()
  const [modalOpen, setModalOpen] = useState(false)

  const handleCreated = useCallback(
    (newWorkspace) => {
      refresh()
    },
    [refresh]
  )

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Page header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-lg font-semibold text-text tracking-tight">Workspaces</h1>
          <p className="text-xs text-text-dim mt-0.5">
            {loading
              ? 'Loading…'
              : workspaces.length > 0
              ? `${workspaces.length} workspace${workspaces.length !== 1 ? 's' : ''}`
              : 'No workspaces'}
          </p>
        </div>

        <button
          onClick={() => setModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 text-xs font-medium bg-surface border border-border rounded hover:border-accent hover:text-accent transition-colors group"
          aria-label="Create workspace"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true" className="group-hover:text-accent">
            <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          New workspace
        </button>
      </div>

      {/* Scan-line atmospheric overlay — the aesthetic risk */}
      <div className="relative">
        <div className="scan-line absolute inset-0 pointer-events-none rounded-xl" aria-hidden="true" />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading && !error
            ? Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="h-44 rounded-lg bg-surface border border-border animate-pulse"
                  aria-hidden="true"
                />
              ))
            : error
            ? <ErrorState message={error} onRetry={refresh} />
            : workspaces.length === 0
            ? <EmptyState onOpen={() => setModalOpen(true)} />
            : workspaces.map((ws) => (
                <WorkspaceCard
                  key={ws.id}
                  workspace={ws}
                  snapshotCount={ws.snapshotCount ?? 0}
                />
              ))}
        </div>
      </div>

      {modalOpen && (
        <CreateWorkspaceModal
          onClose={() => setModalOpen(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  )
}
